import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { AuthService } from '../../../services/auth/auth.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { UserService } from '../../../services/user.service';
import { PlanLimitError } from '../../../features/plans/models/enforcement.model';
import { PlanLimitErrorService } from '../../../features/plans/services/plan-limit-error.service';
import type { ValidateTokenResponse } from './invite-wizard.component';
import { InviteOnboardingTokenVerifier } from './invite-onboarding-token.verifier';

export interface InviteCompletionInput {
  invite: ValidateTokenResponse | null;
  token: string;
  email: string;
  form: FormGroup;
}

export interface InviteCompletionPort {
  setLoading(value: boolean): void;
  setError(message: string): void;
  setPlanLimit(error: PlanLimitError | null): void;
  setStep(step: number): void;
  touchForm(form: FormGroup): void;
  translate(key: string): string;
}

export class InviteOnboardingSubmitter {
  constructor(
    private readonly authService: AuthService,
    private readonly auth0: Auth0Service,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly planLimitErrors: PlanLimitErrorService,
    private readonly logger: FrontendLoggerService,
    private readonly tokenVerifier: InviteOnboardingTokenVerifier
  ) {}

  async submit(input: InviteCompletionInput, port: InviteCompletionPort): Promise<void> {
    port.setLoading(true);
    port.setError('');
    port.setPlanLimit(null);
    if (!this.validate(input, port)) return;

    const payload = {
      inviteId: input.invite!.id,
      token: input.token,
      email: input.email,
      password: input.form.get('password')?.value,
      firstName: input.form.get('firstName')?.value,
      lastName: input.form.get('secondName')?.value,
      language: input.form.get('language')?.value,
      timeZone: input.form.get('timezone')?.value,
    };

    this.userService.acceptInvite(payload).subscribe({
      next: async () => this.finishSuccessfulInvite(port),
      error: (error) => this.handleFailure(error, port),
    });
  }

  private validate(input: InviteCompletionInput, port: InviteCompletionPort): boolean {
    if (!input.invite) {
      port.setError(port.translate('invite.server_error'));
      port.setLoading(false);
      return false;
    }
    const profileValid = ['firstName', 'secondName', 'jobTitle', 'company', 'timezone']
      .every((control) => input.form.get(control)?.valid);
    if (!profileValid) {
      port.touchForm(input.form);
      port.setStep(2);
      port.setLoading(false);
      return false;
    }
    if (!input.form.get('password')?.valid) {
      port.touchForm(input.form);
      port.setStep(3);
      port.setLoading(false);
      return false;
    }
    if (input.form.get('password')?.value !== input.form.get('confirmPassword')?.value) {
      port.setError(port.translate('invite.passwords_not_match'));
      port.setStep(3);
      port.setLoading(false);
      return false;
    }
    return true;
  }

  private async finishSuccessfulInvite(port: InviteCompletionPort): Promise<void> {
    this.authService.setResolvedFlow('registered');
    this.authService.clearPendingInviteContext();
    try {
      const refreshed = await this.tokenVerifier.refreshAndWait(10_000);
      port.setLoading(false);
      if (!refreshed) {
        this.logger.warn(
          '[InviteWizard] silent refresh did not provide app metadata; forcing redirect login'
        );
        try {
          await (this.auth0 as any).loginWithRedirect({ appState: { target: '/home' } });
          return;
        } catch (error) {
          this.logger.error(
            '[InviteWizard] loginWithRedirect failed: ' + (error as any)?.message
          );
        }
      }
    } catch (error) {
      this.logger.error('[InviteWizard] error waiting for token: ' + (error as any)?.message);
    }
    this.router.navigate(['/home']);
  }

  private handleFailure(error: any, port: InviteCompletionPort): void {
    port.setLoading(false);
    const planLimit = this.planLimitErrors.fromHttpError(error, 'invite');
    if (planLimit) {
      port.setPlanLimit(planLimit);
      port.setError(port.translate('enforcement.banner.body'));
      this.logger.warn('[InviteWizard] acceptInvite blocked by plan limit.');
      return;
    }
    const key = error?.status === 401
      ? 'invite.invalid_or_expired'
      : error?.status === 400
        ? 'invite.bad_request'
        : 'invite.server_error';
    port.setError(port.translate(key));
    this.logger.error('[InviteWizard] acceptInvite error: ' + JSON.stringify(error));
  }
}
